import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

export default function LORPlayoffOdds({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${api}/api/lor/intel/playoff-odds`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Could not load playoff odds.'); setLoading(false); });
  }, [api]);

  if (loading) return <div className="lor-loading">Running simulations…</div>;
  if (error)   return <div className="lor-empty">{error}</div>;
  if (!data)   return null;

  const { standings = [], current_week, weeks_remaining, playoff_spots } = data;
  const sorted = [...standings].sort((a, b) => b.playoff_pct - a.playoff_pct);

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="🏆" size={18} /> Schedule-Aware Playoff Odds</h2>
        <div className="lor-page-meta">
          Week {current_week} · {weeks_remaining} weeks remaining · {playoff_spots} playoff spots
        </div>
      </div>

      <div className="lor-intel-meta">
        10,000 outcomes using current projected lineup strength, scoring, recent form, record and the actual remaining Sleeper schedule. · <span className="lor-generated-badge">schedule-aware</span>
      </div>

      <div className="lor-odds-table-wrap">
        <table className="lor-standings-table lor-odds-table">
          <thead>
            <tr>
              <th>RK</th>
              <th>Manager</th>
              <th>W</th>
              <th>L</th>
              <th>PF</th>
              <th>Playoff %</th>
              <th>Champ %</th>
              <th>Sched</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, i) => {
              const isAboveLine = i < playoff_spots;
              const isCutline  = i === playoff_spots - 1;
              const mvDir      = s.movement_dir;

              return (
                <React.Fragment key={s.roster_id}>
                  {i === playoff_spots && (
                    <tr className="lor-odds-cutline-row">
                      <td colSpan={8}>── Playoff cutline ──</td>
                    </tr>
                  )}
                  <tr className={`lor-odds-row${isAboveLine ? ' lor-odds-row--playoff' : ''}`}>
                    <td className="lor-rank">{i + 1}</td>
                    <td>
                      <div className="lor-manager-cell">
                        {s.avatar && (
                          <img
                            src={`https://sleepercdn.com/avatars/thumbs/${s.avatar}`}
                            alt={s.display_name}
                            className="lor-avatar"
                          />
                        )}
                        <div>
                          <div className="lor-manager-name">{s.display_name}</div>
                          {s.clinched && <div className="lor-odds-clinched"><Icon emoji="✓" size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Clinched</div>}
                          {s.eliminated && <div className="lor-odds-eliminated">✗ Eliminated</div>}
                        </div>
                      </div>
                    </td>
                    <td className="lor-wins">{s.wins}</td>
                    <td className="lor-losses">{s.losses}</td>
                    <td className="lor-pf">{s.pf?.toFixed(1)}</td>
                    <td>
                      <div className="lor-odds-pct-cell">
                        <OddsBar pct={s.playoff_pct} isAboveLine={isAboveLine} />
                        <span className="lor-odds-pct-num">{s.playoff_pct?.toFixed(1)}%</span>
                        <MovementArrow dir={mvDir} amount={s.movement} />
                      </div>
                    </td>
                    <td className="lor-odds-champ">{s.championship_pct?.toFixed(1)}%</td>
                    <td>
                      <span className={`lor-odds-sched lor-odds-sched--${s.schedule_difficulty?.toLowerCase()}`}>
                        {s.remaining_sos_rank ? `#${s.remaining_sos_rank} ${s.schedule_difficulty}` : s.schedule_difficulty}
                      </span>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="lor-odds-legend">
        <div className="lor-odds-legend-item">
          <span className="lor-legend-dot lor-legend-dot--playoff" /> In playoff position
        </div>
        <div className="lor-odds-legend-item">
          <span className="lor-odds-mv-arrow lor-odds-mv-arrow--up">↑</span> Odds improved from last week
        </div>
        <div className="lor-odds-legend-item">
          <span className="lor-odds-mv-arrow lor-odds-mv-arrow--down">↓</span> Odds dropped from last week
        </div>
        <div className="lor-odds-legend-item">
          Sched = remaining SOS rank; #1 is hardest
        </div>
      </div>
    </div>
  );
}

function OddsBar({ pct, isAboveLine }) {
  return (
    <div className="lor-odds-bar-wrap">
      <div
        className={`lor-odds-bar${isAboveLine ? ' lor-odds-bar--in' : ''}`}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
}

function MovementArrow({ dir, amount }) {
  if (!dir || dir === 'flat' || Math.abs(amount || 0) < 0.5) {
    return <span className="lor-odds-mv-arrow lor-odds-mv-arrow--flat">—</span>;
  }
  const isUp = dir === 'up';
  return (
    <span className={`lor-odds-mv-arrow lor-odds-mv-arrow--${dir}`} title={`${isUp ? '+' : ''}${amount}% from last week`}>
      {isUp ? `↑${Math.abs(amount)?.toFixed(1)}` : `↓${Math.abs(amount)?.toFixed(1)}`}
    </span>
  );
}
